import { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { overallState, useInbox } from '../../context/InboxContext';
import { CheckState } from '../../models/inbox/inbox.model';
import { timeAgo } from '../../utils/utils';

const LABEL: Record<CheckState, string> = {
  ok: 'All systems normal',
  warn: 'Something needs a look',
  down: 'Something is down',
  unknown: 'Checking systems…',
};

// Vercel-style status light: one dot that says whether Hethera is healthy,
// and the six checks behind it on click.
const StatusLight = () => {
  const { checks, updatedAt } = useInbox();
  const [open, setOpen] = useState(false);
  // Fixed position from the trigger, so the sidebar's overflow can't clip it.
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const state = overallState(checks);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <Wrap ref={ref}>
      <Trigger
        type='button'
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setAnchor({ left: rect.left, bottom: window.innerHeight - rect.top + 6 });
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        title={LABEL[state]}
        className='status-trigger'
      >
        <Dot className={state} />
        <span className='text'>{LABEL[state]}</span>
      </Trigger>
      {open && (
        <Pop role='dialog' aria-label='System status' style={anchor ?? undefined}>
          <strong>System status</strong>
          <ul>
            {checks.map((c) => (
              <li key={c.key}>
                <Dot className={c.state} />
                <span className='label'>{c.label}</span>
                <span className='detail'>{c.detail}</span>
              </li>
            ))}
          </ul>
          {updatedAt && <small>Checked {timeAgo(updatedAt.toISOString())} · every minute</small>}
        </Pop>
      )}
    </Wrap>
  );
};

export default StatusLight;

const Wrap = styled.div`
  position: relative;
  margin: 0 10px 8px;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--hover);
    color: var(--ink);
  }
`;

export const Dot = styled.span`
  position: relative;
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #a9a395;

  &.ok {
    background: #22a35a;
  }
  &.warn {
    background: #d9a21b;
  }
  &.down {
    background: var(--red);
  }
  /* A soft pulse only when something is wrong. */
  &.warn::after,
  &.down::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 999px;
    background: currentColor;
    color: inherit;
    opacity: 0;
    animation: statusPulse 2s ease-out infinite;
  }
  &.warn::after {
    background: #d9a21b;
  }
  &.down::after {
    background: var(--red);
  }

  @keyframes statusPulse {
    0% {
      opacity: 0.35;
      transform: scale(0.6);
    }
    100% {
      opacity: 0;
      transform: scale(1.6);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none !important;
    }
  }
`;

const Pop = styled.div`
  position: fixed;
  z-index: 25000;
  width: 320px;
  padding: 14px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: var(--shadow-lg);
  animation: popIn 0.12s ease-out;

  & strong {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    color: var(--ink);
  }
  & ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  & li {
    display: grid;
    grid-template-columns: 8px auto 1fr;
    white-space: nowrap;
    align-items: center;
    gap: 10px;
    padding: 7px 0;
    border-top: 1px solid #efece4;
    font-size: 12.5px;
  }
  & li:first-of-type {
    border-top: 0;
  }
  & .label {
    color: var(--ink-2);
    font-weight: 500;
  }
  & .detail {
    color: #7a8279;
    text-align: right;
  }
  & small {
    display: block;
    margin-top: 8px;
    font-size: 11.5px;
    color: #a9a395;
  }

  @keyframes popIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
  }
`;
